import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Forwarding, DAY_OF_WEEK_OPTIONS } from './notifications-forwardings.model';
import {
  HLM_DIALOG_SHELL,
  HlmBtnDirective,
  HlmIconComponent,
  HlmInputDirective,
  HlmSelectComponent,
  HlmAlertComponent,
  HLM_FORM_FIELD,
  HlmSelectOption,
} from '../../ui';

export interface ScheduleEditDialogData {
  forwarding: Forwarding;
}

export interface ScheduleEditDialogResult {
  dayOfWeek: number | null;
  startTime: string | null;
  endTime: string | null;
  startDate: string | null;
  endDate: string | null;
}

@Component({
  selector: 'cmt-schedule-edit-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ...HLM_DIALOG_SHELL,
    ...HLM_FORM_FIELD,
    HlmBtnDirective, HlmIconComponent, HlmInputDirective,
    HlmSelectComponent, HlmAlertComponent,
  ],
  template: `
    <hlm-dialog-surface size="md">
      <hlm-dialog-header>
        <hlm-dialog-icon-badge tone="primary">
          <hlm-icon name="lucideClock" size="1.1rem" />
        </hlm-dialog-icon-badge>
        <div hlmDialogHeading>
          <h2 hlmDialogTitle>Edit Schedule</h2>
          <p hlmDialogDescription>{{ data.forwarding.reachability.name }} &mdash; {{ data.forwarding.reachability.destination }}</p>
        </div>
        <button hlmDialogClose (click)="dialogRef.close(null)">
          <hlm-icon name="lucideX" size="1rem" />
        </button>
      </hlm-dialog-header>

      <section hlmDialogBody class="overflow-visible">
        <hlm-alert tone="info" class="mb-4">
          <hlm-icon name="lucideInfo" size="1rem" class="mt-0.5 text-primary" />
          <span>Time restrictions apply per day and are not bound to dates. All fields are optional.</span>
        </hlm-alert>

        <div class="grid grid-cols-2 gap-4">
          <hlm-form-field class="col-span-full">
            <label hlmLabel>Day of week</label>
            <hlm-select
              [options]="daySelectOptions"
              [value]="form.controls.dayOfWeek.value"
              (valueChange)="form.controls.dayOfWeek.setValue($any($event))"
            />
          </hlm-form-field>

          <hlm-form-field>
            <label hlmLabel>Start time (CET)</label>
            <input hlmInput type="time" step="1" [formControl]="form.controls.startTime" placeholder="00:00:00" />
            <p hlmFieldHint>24h format, e.g. 08:00:00</p>
          </hlm-form-field>

          <hlm-form-field>
            <label hlmLabel>End time (CET)</label>
            <input hlmInput type="time" step="1" [formControl]="form.controls.endTime" placeholder="23:59:59" />
            <p hlmFieldHint>24h format, e.g. 17:30:00</p>
          </hlm-form-field>

          <hlm-form-field>
            <label hlmLabel>Start date</label>
            <input hlmInput type="date" [formControl]="form.controls.startDate" />
            <p hlmFieldHint>Optional date range start</p>
          </hlm-form-field>

          <hlm-form-field>
            <label hlmLabel>End date</label>
            <input hlmInput type="date" [formControl]="form.controls.endDate" />
            <p hlmFieldHint>Optional date range end</p>
          </hlm-form-field>
        </div>
      </section>

      <footer hlmDialogFooter>
        <button hlmBtn variant="outline" (click)="dialogRef.close(null)">Cancel</button>
        <button hlmBtn (click)="save()">
          <hlm-icon name="lucideSave" size="0.9rem" />
          Save
        </button>
      </footer>
    </hlm-dialog-surface>
  `,
})
export class ScheduleEditDialogComponent {
  readonly dialogRef = inject(MatDialogRef<ScheduleEditDialogComponent>);
  readonly data: ScheduleEditDialogData = inject(MAT_DIALOG_DATA);
  readonly dayOptions = DAY_OF_WEEK_OPTIONS;
  readonly daySelectOptions: HlmSelectOption<number | null>[] = DAY_OF_WEEK_OPTIONS.map(o => ({
    value: o.value, label: o.label,
  }));

  readonly form = new FormGroup({
    dayOfWeek: new FormControl<number | null>(this.data.forwarding.dayOfWeek),
    startTime: new FormControl<string | null>(this.data.forwarding.startTime),
    endTime: new FormControl<string | null>(this.data.forwarding.endTime),
    startDate: new FormControl<string | null>(this.data.forwarding.startDate),
    endDate: new FormControl<string | null>(this.data.forwarding.endDate),
  });

  save(): void {
    const v = this.form.getRawValue();
    this.dialogRef.close({
      dayOfWeek: v.dayOfWeek,
      startTime: v.startTime || null,
      endTime: v.endTime || null,
      startDate: v.startDate || null,
      endDate: v.endDate || null,
    } as ScheduleEditDialogResult);
  }
}
